import type { Datastore } from 'util/datastore';
import { useModuleMap, useServerCallResult } from 'util/datastore';
import { defineModuleMap } from 'util/moduledata';

export enum SuspensionStatusType {
    WARNED = 'warned',
    UNWARNED = 'unwarned',
    BLOCKED = 'blocked',
    UNBLOCKED = 'unblocked',
}

export type SuspensionObject = {
    // Unique key
    key: string;
    // Time of creation
    time: number;
    // Reciver of suspension
    recipientKey: string;
    // Name of the moderator
    blockedByName?: string | null;
    // What is the current state
    suspensionType: SuspensionStatusType;
    // When does it expires
    expiresAt?: number;
    // Key of the moderator
    from?: string | null;

    // -------------------------> In case it's attached to an object, all should be set
    // Question / Article Key
    instanceKey?: string;
    // Structure (question, topic etc.)
    structureKey?: string;
    // Comment key
    objectKey?: string;
    // Type of the object, currently only comment
    objectType?: string;
};

// Suspension Data of user
const moduleMapSuspensions = defineModuleMap<SuspensionObject>('USER_READ_GLOBAL', 'suspension', 'suspensions');

type FormattedSuspensions = {
    all: SuspensionObject[];
    blocks: SuspensionObject[];
    warnings: SuspensionObject[];
    activeWarnings: SuspensionObject[];
    latestBlock: SuspensionObject | null;
};

// filter all suspensions in easily readable format
export function formatSuspensions(suspensionMap: Record<string, SuspensionObject>): FormattedSuspensions {
    let suspensionsList = suspensionMap ? Object.values(suspensionMap) : [];
    suspensionsList = suspensionsList.sort((a, b) => b.time - a.time);

    const blocksAndUnblocks = suspensionsList.filter(
        (s) => s.suspensionType === SuspensionStatusType.BLOCKED || s.suspensionType == SuspensionStatusType.UNBLOCKED,
    );

    const blocks = suspensionsList.filter(
        (s) => s.suspensionType === SuspensionStatusType.BLOCKED || s.suspensionType === SuspensionStatusType.UNBLOCKED,
    );
    const warnings = suspensionsList.filter(
        (s) => s.suspensionType === SuspensionStatusType.WARNED || s.suspensionType === SuspensionStatusType.UNWARNED,
    );

    const warningsGroupedByObjectKey = new Map<string, SuspensionObject[]>();

    // Determine the active warnings
    for (const suspension of warnings) {
        if (!suspension.objectKey) continue;
        if (!warningsGroupedByObjectKey.has(suspension.objectKey)) {
            warningsGroupedByObjectKey.set(suspension.objectKey, []);
        }
        warningsGroupedByObjectKey.get(suspension.objectKey)?.push(suspension);
    }
    const activeWarnings = Array.from(warningsGroupedByObjectKey.keys()).flatMap((key) => {
        const firstWarning = warningsGroupedByObjectKey.get(key)?.[0];
        return firstWarning?.suspensionType === SuspensionStatusType.WARNED ? [firstWarning] : [];
    });

    return {
        all: suspensionsList,
        blocks,
        warnings,
        activeWarnings: activeWarnings,
        latestBlock: blocksAndUnblocks.length > 0 ? blocksAndUnblocks[0] : null,
    };
}

/** Returns the suspensions of specific user, admin only function */
export function useUserSuspensions({ userKey, ...params }: { userKey: string; params?: any }): FormattedSuspensions {
    const allSuspensions: Record<string, SuspensionObject> | undefined = useServerCallResult('suspension', 'getUserSuspensions', {
        userKey,
        ...params,
    });
    return formatSuspensions(allSuspensions ?? {});
}

/** Returns your own suspensions with minimal data set */
export function useMySuspensions(): FormattedSuspensions {
    const mySuspensions = useModuleMap(moduleMapSuspensions);
    return formatSuspensions(mySuspensions ?? {});
}

export async function setBlockStatusAsync({
    datastore,
    userKey,
    value,
    shouldSendEmail = false,
}: {
    datastore: Datastore;
    userKey: string;
    value: boolean;
    shouldSendEmail?: boolean;
}) {
    await datastore.callServerAsync('suspension', 'setBlockStatus', {
        userKey: userKey,
        value: value,
        shouldSendEmail,
    });
}

export async function setWarnStatusAsync({
    datastore,
    userKey,
    value,
    modTaskKey,
}: {
    datastore: Datastore;
    userKey: string;
    value: boolean;
    modTaskKey: string;
}) {
    await datastore.callServerAsync('suspension', 'warnUser', {
        userKey: userKey,
        value: value,
        modTaskKey: modTaskKey,
    });
}

export function useIsSuspended() {
    const mySuspensions = useMySuspensions();
    return mySuspensions?.latestBlock?.suspensionType === SuspensionStatusType.BLOCKED;
}
